import React, { useState, useEffect } from 'react';
import AdminNavbar from '../components/AdminNavbar';
import axios from 'axios';

const AdminForms = () => {
  const [formCategories, setFormCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState({
    title: '',
    description: '',
    form_pdf: null
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchFormCategories();
  }, []);

  function getCSRFToken() {
    return window.CSRF_TOKEN || '';
  }

  const fetchFormCategories = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get('/api/form-categories', {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-CSRFToken": getCSRFToken(),
        }
      });
      setFormCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching form categories:', error);
      setLoading(false);
    }
  };

  const fetchSubmissions = async (categoryId) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.get(`/api/form-categories/${categoryId}/submissions`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-CSRFToken": getCSRFToken(),
        }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('title', newCategory.title);
    formData.append('description', newCategory.description);
    formData.append('form_pdf', newCategory.form_pdf);

    try {
      const authToken = localStorage.getItem('authToken');
      await axios.post('/api/form-categories', formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-CSRFToken": getCSRFToken(),
          'Content-Type': 'multipart/form-data',
        }
      });

      setMessage({ type: 'success', text: 'Form category added successfully!' });
      setShowAddModal(false);
      setNewCategory({ title: '', description: '', form_pdf: null });
      fetchFormCategories();
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to add category' });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this form category? All submissions will also be deleted.')) {
      return;
    }

    try {
      const  authToken = localStorage.getItem('authToken');
      await axios.delete(`/api/form-categories/${categoryId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-CSRFToken": getCSRFToken(),
        }
      });

      setMessage({ type: 'success', text: 'Form category deleted successfully!' });
      fetchFormCategories();
      if (selectedCategory?.id === categoryId) {
        setSelectedCategory(null);
        setSubmissions([]);
      }
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete category' });
    }
  };

  const handleToggleStatus = async (categoryId) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axios.post(`/api/form-categories/${categoryId}/toggle-status`, {}, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-CSRFToken": getCSRFToken(),
        }
      });

      setMessage({ type: 'success', text: response.data.message });
      fetchFormCategories();
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to toggle status' });
    }
  };

  const handleViewSubmissions = (category) => {
    setSelectedCategory(category);
    fetchSubmissions(category.id);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-gray-100 min-h-screen">
        <AdminNavbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-blue-800">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <AdminNavbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-blue-800">Form Management</h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
          >
            Add New Form Category
          </button>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {!selectedCategory ? (
          <div>
            {formCategories.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <p className="text-gray-600 text-lg">No form categories available. Add one to get started.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {formCategories.map((category) => (
                  <div key={category.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-semibold text-blue-800">
                        {category.title}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        category.is_enabled 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {category.is_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {category.description}
                    </p>
                    <div className="mb-4">
                      <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                        {category.submission_count} Submission{category.submission_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewSubmissions(category)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm transition-colors"
                        >
                          View Submissions
                        </button>
                        <a
                          href={category.form_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm text-center transition-colors"
                        >
                          View Form
                        </a>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleToggleStatus(category.id)}
                          className={`flex-1 ${
                            category.is_enabled 
                              ? 'bg-orange-600 hover:bg-orange-700' 
                              : 'bg-green-600 hover:bg-green-700'
                          } text-white px-4 py-2 rounded-md text-sm transition-colors`}
                        >
                          {category.is_enabled ? 'Disable Form' : 'Enable Form'}
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-6">
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setSubmissions([]);
                }}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Categories
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-2xl font-bold text-blue-800 mb-2">
                {selectedCategory.title}
              </h3>
              <p className="text-gray-600 mb-4">
                {selectedCategory.description}
              </p>
              <div className="flex gap-4">
                <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-medium">
                  Total Submissions: {submissions.length}
                </span>
                <a
                  href={selectedCategory.form_pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
                >
                  View Form PDF
                </a>
              </div>
            </div>

            {submissions.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <p className="text-gray-600 text-lg">No submissions yet for this category.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Applicant Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Phone
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Submitted At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {submissions.map((submission) => (
                        <tr key={submission.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {submission.applicant_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {submission.applicant_email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {submission.applicant_phone}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(submission.submitted_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <a
                              href={submission.submitted_file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors inline-block"
                            >
                              View File
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-blue-800">Add New Form Category</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewCategory({ title: '', description: '', form_pdf: null });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddCategory} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Form Title
                </label>
                <input
                  type="text"
                  value={newCategory.title}
                  onChange={(e) => setNewCategory({ ...newCategory, title: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Literary Award Application"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  required
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Describe the form and requirements..."
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Form PDF
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setNewCategory({ ...newCategory, form_pdf: e.target.files[0] })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Upload the registration form in PDF format
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewCategory({ title: '', description: '', form_pdf: null });
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-md font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium transition-colors"
                >
                  Add Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminForms;
